// ============================================================================
// WORKSHOP SERVICE - frontend/src/services/workshop.ts
// API calls for workshop functionality
// ============================================================================

import { api } from './api';

export interface AvatarSuggestion {
  role: string;
  name: string;
  response: string;
  preview: string;
  urgency: 'urgent' | 'important' | 'normal';
  audioBase64?: string;
  audioUrl?: string;
  audioS3Key?: string; // S3 key for persisted TTS audio — survives across sessions
  audioGenerating?: boolean; // True while async TTS is in progress
  governorReason: string;
  governorPriority: number;
  state?: 'pending' | 'accepted' | 'ignored' | 'rejected'; // Persisted state
  contributionId?: string; // Unique ID for graph feedback
  avatarDefinitionId?: string; // Avatar definition ID for learning
  createdAt?: string; // ISO timestamp when contribution was created
}

export interface FacilitatorGuidance {
  timestamp: string;
  phase: string;
  summary: string;
  missingPerspectives: Array<{
    avatar: string;
    reason: string;
    importance: 'critical' | 'important' | 'nice-to-have';
  }>;
  conversationHealth: {
    balanceScore: number;
    engagementLevel: 'high' | 'medium' | 'low';
    concerns: string[];
  };
  suggestedPrompts: string[];
  nextStepRecommendation: string;
}

export interface PersonaBackstory {
  age?: string;
  gender?: string;
  education?: string;
  familySituation?: string;
  background?: string;
  traits: string[];
}

export interface ExtractedPersona {
  id: string;
  name: string;
  role: string;
  description: string;
  goals: string[];
  painPoints: string[];
  backstory?: PersonaBackstory;
  // Accumulative tracking metadata
  firstSeen?: string;
  lastSeen?: string;
  isNew?: boolean;
  originalId?: string; // Track the original LLM-generated ID
}

export interface UserStory {
  id: string;
  persona: string;
  story: string;
  acceptanceCriteria: string[];
  priority: 'high' | 'medium' | 'low';
  approvalStatus?: 'pending' | 'approved' | 'rejected';
  rank?: number;
  tags?: string[];
  // Accumulative tracking metadata
  firstSeen?: string;
  lastSeen?: string;
  isNew?: boolean;
  originalId?: string; // Track the original LLM-generated ID
}

export interface TechnicalSpecs {
  coreFunctionality: string[];
  integrationPoints: string[];
  dataRequirements: string[];
  securityConsiderations: string[];
  performanceTargets: string[];
}

export interface Constraints {
  technical: string[];
  business: string[];
  regulatory: string[];
}

export interface NFR {
  id: string;
  category: 'performance' | 'security' | 'scalability' | 'availability' | 'usability' | 'maintainability' | 'compliance';
  title: string;
  description: string;
  priority: 'high' | 'medium' | 'low';
  acceptanceCriteria: string[];
}

export interface ProductRequirements {
  projectName: string;
  generatedAt: string;
  sessionId: string;
  personas: ExtractedPersona[];
  userStories: UserStory[];
  technicalSpecs: TechnicalSpecs;
  constraints: Constraints;
  assumptions: string[];
  nonFunctionalRequirements?: NFR[];
}

export interface RequirementsExtractionResponse {
  success: boolean;
  sessionId: string;
  requirements: ProductRequirements;
  timestamp: string;
}

/**
 * Generate TTS audio for avatar contribution (called on-demand when user accepts)
 */
export async function generateTTS(
  text: string,
  avatarRole: string
): Promise<{ success: boolean; audioBase64: string; audioUrl: string; format: string; durationMs: number }> {
  const response = await api.post('/api/sessions/workshop/generate-tts', {
    text,
    avatarRole
  });

  return response.data;
}

export async function approveAvatarContribution(
  sessionId: string,
  avatarRole: string,
  text: string,
  audioBase64: string
): Promise<{ success: boolean; playbackMode: string; message: string; audioBase64?: string }> {
  const response = await api.post('/api/sessions/workshop/approve-avatar', {
    sessionId,
    avatarRole,
    text,
    audioBase64
  });

  return response.data;
}

/**
 * Get facilitator guidance for current phase
 */
export async function getFacilitatorGuidance(
  sessionId: string,
  phase: string,
  phaseObjective: string,
  phaseGoal?: string,
  successCriteria?: string[]
): Promise<FacilitatorGuidance> {
  const response = await api.post('/api/sessions/workshop/guidance', {
    sessionId,
    phase,
    phaseObjective,
    phaseGoal,
    successCriteria
  });

  return response.data;
}

/**
 * Extract current requirements from session (non-destructive, can be called periodically)
 */
export async function extractRequirements(
  sessionId: string,
  transcript: any[],
  customerName?: string,
  personas?: any[],
  guidance?: any[]
): Promise<RequirementsExtractionResponse> {
  const response = await api.post(`/api/sessions/${sessionId}/extract-requirements`, {
    transcript,
    customerName,
    personas,
    guidance
  });

  return response.data;
}

/**
 * Get stored requirements for a session
 * Returns null if no requirements have been extracted yet
 */
export async function getStoredRequirements(
  sessionId: string
): Promise<ProductRequirements | null> {
  try {
    const response = await api.get(`/api/sessions/${sessionId}/requirements`);
    return response.data.requirements;
  } catch (error: any) {
    // 404 means no requirements stored yet
    if (error.response?.status === 404) {
      return null;
    }
    console.error('Failed to get stored requirements:', error);
    return null;
  }
}

export async function clearRequirements(sessionId: string): Promise<void> {
  await api.delete(`/api/sessions/${sessionId}/requirements`);
}

export async function extractNFRs(
  sessionId: string,
  customerName?: string
): Promise<{ success: boolean; sessionId: string; task: any }> {
  const response = await api.post(`/api/sessions/${sessionId}/extract-nfrs`, {
    customerName
  });
  return response.data;
}
